import { LINKS } from "../../config/links";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-32 pb-20 overflow-hidden flex items-center"
    >
      {/* Background — cinematic dark with subtle motion */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/3a625aa1-2864-4d34-9680-b989d3145672/images/05efca474d39e17a577e49cb4253d87ab98685297274e6b9d49cd30682e8c309.png"
          alt=""
          aria-hidden
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1733]/95 via-[#0f2246]/80 to-[#15294e]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1733]/95 via-transparent to-[#0a1733]/50" />
        <div className="absolute inset-0 noise-overlay" aria-hidden />
      </div>

      <div className="w-full max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="reveal flex items-center gap-3 mb-8">
            <span className="h-[1px] w-10 bg-amber-500/70" />
            <span className="font-sans-body uppercase text-[11px] tracking-[0.32em] text-amber-400">
              Systems · Automation · Emotional Intelligence
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="reveal font-display font-light text-white tracking-[-0.01em] text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] text-balance"
            style={{ transitionDelay: "60ms" }}
          >
            Most advisors don't have a lead problem.{" "}
            <span className="italic text-amber-400 font-normal">
              They have a systems problem.
            </span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="reveal mt-7 max-w-xl text-slate-300 text-base sm:text-lg leading-relaxed"
            style={{ transitionDelay: "140ms" }}
          >
            Helping financial advisors and digital entrepreneurs automate
            follow-up, improve conversion, reduce no-shows, and build
            predictable growth — through emotionally intelligent systems
            and automation.
          </p>

          <div
            className="reveal mt-10 flex flex-col sm:flex-row gap-4"
            style={{ transitionDelay: "220ms" }}
          >
            <a
              href={LINKS.calendar}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-3 bg-amber-600 hover:bg-amber-500 text-slate-950 font-semibold px-8 py-4 text-[13px] uppercase tracking-[0.18em] transition-all duration-300 hover:-translate-y-0.5"
            >
              Book A Strategy Session
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="transition-transform group-hover:translate-x-1">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </a>
            <a
              href="#start-here"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-3 border border-white/20 hover:border-amber-400/60 text-white hover:text-amber-300 px-8 py-4 text-[13px] uppercase tracking-[0.18em] transition-all duration-300 bg-white/[0.02] hover:bg-white/[0.04]"
            >
              Explore Systems
            </a>
          </div>

          <p
            className="reveal mt-5 text-xs text-slate-400 italic"
            style={{ transitionDelay: "280ms" }}
          >
            No pressure. Just clarity. Let's map your system together.
          </p>

          <div
            className="reveal mt-8 flex flex-wrap gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.2em] text-slate-400"
            style={{ transitionDelay: "340ms" }}
          >
            <span className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-amber-500" />
              Built by an advisor
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-amber-500" />
              For advisors
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-amber-500" />
              Operational relief
            </span>
          </div>
        </div>

        {/* Stat focal point */}
        <div className="lg:col-span-5">
          <div
            data-testid="hero-stat-card"
            className="reveal relative bg-gradient-to-br from-white/[0.05] to-white/[0.01] border border-white/10 backdrop-blur-xl p-8 sm:p-10 overflow-hidden"
            style={{ transitionDelay: "360ms" }}
          >
            <span className="absolute top-0 left-0 h-8 w-8 border-t-2 border-l-2 border-amber-500" aria-hidden />
            <span className="absolute bottom-0 right-0 h-8 w-8 border-b-2 border-r-2 border-amber-500/40" aria-hidden />

            <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 mb-2">
              You Are Not Failing
            </div>
            <div className="text-xs text-slate-500 mb-8">
              Your systems are.
            </div>

            <div className="flex items-baseline gap-4 mb-2">
              <span className="font-display text-6xl sm:text-7xl text-slate-600 line-through decoration-rose-500/50 decoration-[3px]">
                29
              </span>
              <svg width="24" height="12" viewBox="0 0 24 12" className="text-amber-500">
                <path d="M2 6h20M17 2l5 4-5 4" fill="none" stroke="currentColor" strokeWidth="1.5" />
              </svg>
              <span className="font-display text-7xl sm:text-8xl text-amber-400 leading-none">
                3
              </span>
            </div>
            <div className="text-sm text-slate-300">
              <span className="font-semibold text-white">No-shows after automation</span>{" "}
              — a{" "}
              <span className="bg-amber-500/20 text-amber-300 px-2 py-0.5 font-bold rounded-sm">
                −90%
              </span>{" "}
              drop once the system did the chasing. <span className="text-amber-300">3 in one day</span> isn't a fluke — it's an operating rhythm.
            </div>

            <div className="mt-10 pt-6 border-t border-white/10 grid grid-cols-3 gap-4">
              <MiniStat label="Leads" before="65" after="17" />
              <MiniStat label="Appts" before="36" after="20" />
              <MiniStat label="Attended" before="18" after="10" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, before, after }) {
  return (
    <div>
      <div className="font-sans-body uppercase text-[9px] tracking-[0.24em] text-slate-500 mb-2">
        {label}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-sm text-slate-600 line-through">{before}</span>
        <span className="font-display text-2xl text-white">{after}</span>
      </div>
    </div>
  );
}
